import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { registerUser, verifyEmail, resendVerificationCode } from '../api/Auth';
import { getAllProfessions, Profession } from '../api/Professions';
import SEOHelmet from '../components/SEOHelmet';
import { trackSignUp, trackEmailVerified } from '../utils/analytics';

const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface-raised)',
  border: '1px solid var(--color-border)',
  borderTop: '4px solid var(--color-primary)',
  borderRadius: 'var(--radius-md)',
  boxShadow: 'var(--shadow-md)',
  padding: '2.5rem 2rem',
  width: '100%',
  maxWidth: '460px',
};

const labelStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 'var(--fw-semibold)',
  fontSize: 'var(--fs-sm)',
  color: 'var(--color-text)',
  display: 'block',
  marginBottom: '0.375rem',
};

const inputStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontSize: 'var(--fs-sm)',
  color: 'var(--color-text)',
  background: 'var(--color-surface-raised)',
  border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-sm)',
  width: '100%',
  padding: '0.55rem 0.85rem',
  outline: 'none',
  transition: 'border-color var(--transition-fast), box-shadow var(--transition-fast)',
};

const primaryBtnStyle: React.CSSProperties = {
  fontFamily: 'var(--font-body)',
  fontWeight: 'var(--fw-semibold)',
  fontSize: 'var(--fs-base)',
  background: 'var(--color-primary)',
  color: 'var(--color-text-inverse)',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  width: '100%',
  padding: '0.7rem 1rem',
  cursor: 'pointer',
  marginTop: '0.5rem',
  transition: 'background var(--transition-fast)',
};

// ── Plan intent data ──────────────────────────────────────────────────────────
interface PlanMeta { label: string; price: number; seats?: string; icon: string }

function getPlanMeta(plan: string, tier: string | null, interval: string): PlanMeta | null {
  if (!plan) return null;
  const ann = interval === 'annual';
  if (plan === 'solo') return { label: 'Solo Plan', price: ann ? 2 : 3, icon: 'bi-person-fill' };
  if (plan === 'pro') return { label: 'Pro Plan', price: ann ? 4 : 5, icon: 'bi-person-check-fill' };
  const tiers: Record<string, { monthly: number; annual: number; seats: string }> = {
    Micro:    { monthly: 30, annual: 24, seats: 'Up to 5 members' },
    Starter:  { monthly: 40, annual: 36, seats: 'Up to 10 members' },
    Firm:     { monthly: 50, annual: 48, seats: 'Up to 30 members' },
    Practice: { monthly: 249, annual: 199, seats: 'Up to 100 members' },
  };
  const t = tier || 'Starter';
  const info = tiers[t] || tiers.Starter;
  return {
    label: `Team — ${t} Plan`,
    price: ann ? info.annual : info.monthly,
    seats: info.seats,
    icon: t === 'Practice' ? 'bi-buildings-fill' : 'bi-buildings',
  };
}

// ── PlanIntentBanner ─────────────────────────────────────────────────────────
const PlanIntentBanner: React.FC<{ meta: PlanMeta; interval: string }> = ({ meta, interval }) => (
  <div style={{
    display: 'flex', alignItems: 'flex-start', gap: '0.875rem',
    background: 'var(--color-accent-light)',
    border: '1px solid var(--color-accent)',
    borderRadius: 'var(--radius-md)',
    padding: '0.875rem 1rem',
    marginBottom: '1.5rem',
  }}>
    <i className={`bi ${meta.icon}`} style={{ fontSize: '1.25rem', color: 'var(--color-accent)', flexShrink: 0, marginTop: '0.1rem' }} />
    <div>
      <div style={{ fontFamily: 'var(--font-body)', fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-sm)', color: 'var(--color-primary)' }}>
        {meta.label}
      </div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
        ${meta.price}/mo{interval === 'annual' ? ' · billed annually' : ''}
        {meta.seats ? ` · ${meta.seats}` : ''}
      </div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>
        Checkout opens after you verify your email.
      </div>
    </div>
  </div>
);

const Register = () => {
  const [searchParams] = useSearchParams();
  const intentPlan     = searchParams.get('plan') || '';
  const intentTier     = searchParams.get('tier') || '';
  const intentInterval = searchParams.get('interval') || 'monthly';
  const planMeta = getPlanMeta(intentPlan, intentTier, intentInterval);

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '',
    password: '', confirmPassword: '', professionID: '',
    website: '', // honeypot — must stay empty; hidden from real users
  });
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [verificationResult, setVerificationResult] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending, setResending] = useState(false);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const navigate = useNavigate();

  const { firstName, lastName, email, password, confirmPassword, professionID, website } = formData;

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  useEffect(() => {
    getAllProfessions().then(setProfessions).catch(() => setProfessions([]));
  }, []);

  useEffect(() => {
    if (resendCooldown > 0) {
      const t = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [resendCooldown]);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (password.length < 8)  { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (!professionID)        { setError('Please select a profession.'); return; }
    setSubmitting(true);
    try {
      await registerUser({ firstName, lastName, email, password, professionID, website });
      trackSignUp(professionID);
      setSuccess('Registration successful! A verification code has been sent to your email.');
      setShowVerification(true);
      setVerificationEmail(email);
      setResendCooldown(60);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Registration failed.';
      setError('Error: ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setVerificationResult(''); setVerifying(true);
    try {
      await verifyEmail({ email: verificationEmail, code: verificationCode });
      trackEmailVerified();
      setVerificationResult('Email verified! Redirecting to sign in…');
      // Preserve plan intent through to login
      const loginPath = intentPlan
        ? `/login?plan=${intentPlan}${intentTier ? `&tier=${intentTier}` : ''}&interval=${intentInterval}`
        : '/login';
      setTimeout(() => navigate(loginPath), 2000);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Verification failed.';
      setVerificationResult('Error: ' + msg);
    } finally {
      setVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setVerificationResult(''); setResending(true);
    try {
      await resendVerificationCode(verificationEmail);
      setVerificationResult('A new code has been sent to your email.');
      setResendCooldown(60);
    } catch (err: any) {
      const msg = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to resend.';
      setVerificationResult((err.response?.status === 429 ? '⚠️ ' : 'Error: ') + msg);
    } finally {
      setResending(false);
    }
  };

  const focusedInput = (field: string): React.CSSProperties => ({
    ...inputStyle,
    borderColor: focusedField === field ? 'var(--color-border-focus)' : 'var(--color-border)',
    boxShadow:   focusedField === field ? '0 0 0 3px rgba(42, 191, 191, 0.18)' : 'none',
  });

  const focus = (field: string) => () => setFocusedField(field);
  const blur  = () => setFocusedField(null);

  // "Sign in" link preserves plan intent params
  const loginHref = intentPlan
    ? `/login?plan=${intentPlan}${intentTier ? `&tier=${intentTier}` : ''}&interval=${intentInterval}`
    : '/login';

  return (
    <>
    <SEOHelmet
      noindex={true}
      title="Create Your Free Account"
      description="Sign up for CEC Track — the free continuing education credit tracker for architects, engineers, CPAs, nurses, and other licensed professionals."
      canonical="https://cectrack.com/register"
    />
    <div style={{
      minHeight: '80vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      background: 'var(--color-surface)',
    }}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ marginBottom: planMeta ? '1.25rem' : '1.75rem', textAlign: 'center' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 'var(--fw-black)',
            fontSize: 'var(--fs-3xl)',
            color: 'var(--color-text)',
            margin: '0 0 0.375rem',
            lineHeight: 1.1,
          }}>
            {showVerification ? 'Verify Email' : 'Create account'}
          </h1>
          <p style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)',
            color: 'var(--color-text-muted)',
            margin: 0,
          }}>
            {showVerification
              ? `Enter the code sent to ${verificationEmail}`
              : planMeta
              ? 'Create your account to continue to checkout'
              : 'Start tracking your CE credits today'}
          </p>
        </div>

        {/* Plan intent banner */}
        {planMeta && !showVerification && (
          <PlanIntentBanner meta={planMeta} interval={intentInterval} />
        )}

        {/* Alerts */}
        {error && (
          <div style={{
            marginBottom: '1.25rem', padding: '0.7rem 1rem',
            background: 'var(--color-danger-light)', border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)', color: 'var(--color-danger)',
          }}>
            <i className="bi bi-exclamation-circle me-2" />{error}
          </div>
        )}
        {success && !showVerification && (
          <div style={{
            marginBottom: '1.25rem', padding: '0.7rem 1rem',
            background: 'var(--color-success-light)', border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-sm)', fontFamily: 'var(--font-body)',
            fontSize: 'var(--fs-sm)', color: 'var(--color-success)',
          }}>
            <i className="bi bi-check-circle me-2" />{success}
          </div>
        )}

        {/* ── Registration Form ── */}
        {!showVerification ? (
          <form onSubmit={onSubmit}>
            {/* Honeypot — invisible to real users, catches naive bots that fill every input */}
            <div style={{ position: 'absolute', left: '-9999px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }} aria-hidden="true">
              <label htmlFor="website">Website</label>
              <input
                type="text" id="website" name="website" value={website} onChange={onChange}
                tabIndex={-1} autoComplete="off"
              />
            </div>

            {/* Profession */}
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={labelStyle}>Profession</label>
              <select
                name="professionID"
                value={professionID}
                onChange={onChange}
                required
                style={{
                  ...focusedInput('profession'),
                  appearance: 'auto',
                }}
                onFocus={focus('profession')}
                onBlur={blur}
              >
                <option value="">Select a profession</option>
                {professions.filter(p => p.isActive !== false).map(p => (
                  <option key={p.professionID} value={p.professionID}>{p.name}</option>
                ))}
              </select>
            </div>

            {/* Name row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.1rem' }}>
              <div>
                <label style={labelStyle}>First name</label>
                <input
                  type="text" name="firstName" value={firstName} onChange={onChange} required
                  style={focusedInput('firstName')} onFocus={focus('firstName')} onBlur={blur}
                  autoComplete="given-name"
                />
              </div>
              <div>
                <label style={labelStyle}>Last name</label>
                <input
                  type="text" name="lastName" value={lastName} onChange={onChange} required
                  style={focusedInput('lastName')} onFocus={focus('lastName')} onBlur={blur}
                  autoComplete="family-name"
                />
              </div>
            </div>

            {/* Email */}
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={labelStyle}>Email address</label>
              <input
                type="email" name="email" value={email} onChange={onChange} required
                placeholder="you@example.com"
                style={focusedInput('email')} onFocus={focus('email')} onBlur={blur}
                autoComplete="email"
              />
            </div>

            {/* Password */}
            <div style={{ marginBottom: '1.1rem' }}>
              <label style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'} name="password"
                  value={password} onChange={onChange} minLength={8} required
                  style={{ ...focusedInput('password'), paddingRight: '2.8rem' }}
                  onFocus={focus('password')} onBlur={blur}
                  autoComplete="new-password"
                />
                <button
                  type="button" aria-label="Toggle password visibility"
                  onClick={() => setShowPassword(v => !v)}
                  style={{
                    position: 'absolute', right: '0.75rem', top: '50%',
                    transform: 'translateY(-50%)', background: 'none', border: 'none',
                    cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0.2rem',
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                </button>
              </div>
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: '0.3rem' }}>
                Must be at least 8 characters
              </div>
            </div>

            {/* Confirm Password */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={labelStyle}>Confirm password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword"
                  value={confirmPassword} onChange={onChange} minLength={8} required
                  style={{ ...focusedInput('confirmPassword'), paddingRight: '2.8rem' }}
                  onFocus={focus('confirmPassword')} onBlur={blur}
                  autoComplete="new-password"
                />
                <button
                  type="button" aria-label="Toggle confirm password visibility"
                  onClick={() => setShowConfirmPassword(v => !v)}
                  style={{
                    position: 'absolute', right: '0.75rem', top: '50%',
                    transform: 'translateY(-50%)', background: 'none', border: 'none',
                    cursor: 'pointer', color: 'var(--color-text-muted)', padding: '0.2rem',
                    display: 'flex', alignItems: 'center',
                  }}
                >
                  <i className={`bi ${showConfirmPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
                </button>
              </div>
            </div>

            <button
              type="submit" disabled={submitting}
              style={{ ...primaryBtnStyle, opacity: submitting ? 0.7 : 1, cursor: submitting ? 'not-allowed' : 'pointer' }}
              onMouseEnter={e => !submitting && ((e.currentTarget as HTMLButtonElement).style.background = 'var(--color-primary-hover)')}
              onMouseLeave={e => ((e.currentTarget as HTMLButtonElement).style.background = 'var(--color-primary)')}
            >
              {submitting ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" />Creating account…</>
              ) : planMeta ? 'Create account & continue to checkout' : 'Create account'}
            </button>
          </form>
        ) : (
          /* ── Verification Form ── */
          <form onSubmit={handleVerify}>
            {/* Plan intent reminder during verification */}
            {planMeta && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                background: 'var(--color-accent-light)', borderRadius: 'var(--radius-sm)',
                padding: '0.5rem 0.75rem', marginBottom: '1.25rem',
                fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)',
              }}>
                <i className={`bi ${planMeta.icon}`} style={{ color: 'var(--color-accent)' }} />
                <span>Verify your email to continue to <strong style={{ color: 'var(--color-primary)' }}>{planMeta.label}</strong> checkout</span>
              </div>
            )}

            <div style={{ marginBottom: '1.25rem' }}>
              <label style={labelStyle}>Verification code</label>
              <input
                type="text"
                value={verificationCode}
                onChange={e => setVerificationCode(e.target.value)}
                required maxLength={6} pattern="[0-9]{6}" placeholder="6-digit code" autoFocus
                style={{
                  ...focusedInput('code'),
                  fontFamily: 'var(--font-mono)',
                  fontSize: 'var(--fs-xl)',
                  textAlign: 'center',
                  letterSpacing: '0.3em',
                  paddingRight: '0.85rem',
                }}
                onFocus={focus('code')} onBlur={blur}
              />
              <div style={{ fontFamily: 'var(--font-body)', fontSize: 'var(--fs-xs)', color: 'var(--color-text-muted)', marginTop: '0.4rem' }}>
                <i className="bi bi-envelope me-1" />
                Check your spam/junk folder if you don't see it.
              </div>
            </div>

            <button
              type="submit" disabled={verifying}
              style={{ ...primaryBtnStyle, background: 'var(--color-success)', opacity: verifying ? 0.7 : 1, cursor: verifying ? 'not-allowed' : 'pointer', marginBottom: '0.75rem' }}
            >
              {verifying ? (
                <><span className="spinner-border spinner-border-sm me-2" role="status" />Verifying…</>
              ) : 'Verify email'}
            </button>

            <div style={{ textAlign: 'center', fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)' }}>
              {resendCooldown > 0 ? (
                <span style={{ color: 'var(--color-text-muted)' }}>Resend code in {resendCooldown}s</span>
              ) : (
                <button
                  type="button" onClick={handleResendCode} disabled={resending}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--color-accent)', fontFamily: 'var(--font-body)',
                    fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-medium)', padding: 0,
                  }}
                >
                  {resending ? 'Sending…' : "Didn't receive a code? Resend"}
                </button>
              )}
            </div>

            {verificationResult && (
              <div style={{
                marginTop: '1rem', padding: '0.7rem 1rem',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
                background: verificationResult.startsWith('Email verified') || verificationResult.startsWith('A new')
                  ? 'var(--color-success-light)' : verificationResult.startsWith('⚠️')
                  ? 'var(--color-warning-light)' : 'var(--color-danger-light)',
                border: `1px solid ${
                  verificationResult.startsWith('Email verified') || verificationResult.startsWith('A new')
                    ? 'var(--color-success)' : verificationResult.startsWith('⚠️')
                    ? 'var(--color-warning)' : 'var(--color-danger)'
                }`,
                color: verificationResult.startsWith('Email verified') || verificationResult.startsWith('A new')
                  ? 'var(--color-success)' : verificationResult.startsWith('⚠️')
                  ? 'var(--color-accent-warm-hover)' : 'var(--color-danger)',
              }}>
                {verificationResult}
              </div>
            )}
          </form>
        )}

        {/* Footer */}
        {!showVerification && (
          <div style={{
            marginTop: '1.5rem', textAlign: 'center',
            fontFamily: 'var(--font-body)', fontSize: 'var(--fs-sm)',
            color: 'var(--color-text-muted)',
          }}>
            Already have an account?{' '}
            <Link to={loginHref} style={{ color: 'var(--color-accent)', fontWeight: 'var(--fw-semibold)', textDecoration: 'none' }}>
              Sign in
            </Link>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default Register;
